// src/pages/dashboards/components/DashboardFormModal.tsx
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Label,
  Textarea,
} from '@/components/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { SwitchWithLabel } from '@/components/ui/Switch';
import { useCreateDashboard, useUpdateDashboard } from '@/hooks/queries/useReportes';
import { PeriodoReporte } from '@/types/reporte';
import type { DashboardListItem } from '@/types/reporte';

const PERIODO_LABELS: Record<PeriodoReporte, string> = {
  [PeriodoReporte.HOY]: 'Hoy',
  [PeriodoReporte.AYER]: 'Ayer',
  [PeriodoReporte.SEMANA]: 'Esta semana',
  [PeriodoReporte.SEMANA_PASADA]: 'Semana pasada',
  [PeriodoReporte.MES]: 'Este mes',
  [PeriodoReporte.MES_PASADO]: 'Mes pasado',
  [PeriodoReporte.TRIMESTRE]: 'Este trimestre',
  [PeriodoReporte.ANO]: 'Este ano',
  [PeriodoReporte.PERSONALIZADO]: 'Personalizado',
};

const schema = z.object({
  nombre: z.string().min(1, 'Nombre requerido').max(100, 'Maximo 100 caracteres'),
  descripcion: z.string().max(500, 'Maximo 500 caracteres').optional(),
  periodo_default: z.nativeEnum(PeriodoReporte),
  es_compartido: z.boolean(),
  auto_refresh_segundos: z.coerce.number().min(0).max(3600).optional(),
});

type FormData = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onClose: () => void;
  dashboard?: DashboardListItem;
}

export function DashboardFormModal({ open, onClose, dashboard }: Props) {
  const createMutation = useCreateDashboard();
  const updateMutation = useUpdateDashboard();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      nombre: '',
      descripcion: '',
      periodo_default: PeriodoReporte.MES,
      es_compartido: false,
      auto_refresh_segundos: undefined,
    },
  });

  const periodoDefault = watch('periodo_default');
  const esCompartido = watch('es_compartido');

  useEffect(() => {
    if (dashboard) {
      reset({
        nombre: dashboard.nombre,
        descripcion: dashboard.descripcion ?? '',
        periodo_default: PeriodoReporte.MES, // El ListItem no tiene periodo_default
        es_compartido: dashboard.es_compartido,
        auto_refresh_segundos: undefined,
      });
    } else {
      reset({
        nombre: '',
        descripcion: '',
        periodo_default: PeriodoReporte.MES,
        es_compartido: false,
        auto_refresh_segundos: undefined,
      });
    }
  }, [dashboard, reset, open]);

  const onSubmit = async (data: FormData) => {
    try {
      const payload = {
        nombre: data.nombre,
        descripcion: data.descripcion || undefined,
        periodo_default: data.periodo_default,
        es_compartido: data.es_compartido,
        auto_refresh_segundos: data.auto_refresh_segundos || undefined,
      };

      if (dashboard) {
        await updateMutation.mutateAsync({
          id: dashboard.id,
          data: payload,
        });
      } else {
        await createMutation.mutateAsync(payload);
      }
      onClose();
    } catch {
      // Error handling is done by react-query
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {dashboard ? 'Editar Dashboard' : 'Nuevo Dashboard'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="nombre">Nombre *</Label>
            <Input
              id="nombre"
              {...register('nombre')}
              placeholder="Mi Dashboard"
              className="mt-1"
            />
            {errors.nombre && (
              <p className="text-sm text-red-500 mt-1">{errors.nombre.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="descripcion">Descripción</Label>
            <Textarea
              id="descripcion"
              {...register('descripcion')}
              placeholder="Descripción opcional del dashboard..."
              rows={3}
              className="mt-1"
            />
            {errors.descripcion && (
              <p className="text-sm text-red-500 mt-1">{errors.descripcion.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="periodo_default">Periodo por defecto</Label>
            <Select
              value={periodoDefault}
              onValueChange={(value) => setValue('periodo_default', value as PeriodoReporte)}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Seleccionar periodo" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PERIODO_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="auto_refresh_segundos">Auto-refresh (segundos)</Label>
            <Input
              id="auto_refresh_segundos"
              type="number"
              {...register('auto_refresh_segundos')}
              placeholder="0 = desactivado"
              min={0}
              max={3600}
              className="mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">
              Deja en 0 o vacio para desactivar. Maximo 3600 segundos (1 hora).
            </p>
          </div>

          <div className="pt-2">
            <SwitchWithLabel
              checked={esCompartido}
              onCheckedChange={(checked) => setValue('es_compartido', checked)}
              label="Compartir dashboard"
              description="Otros usuarios podran ver este dashboard"
            />
          </div>

          <DialogFooter className="pt-4">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? 'Guardando...'
                : dashboard
                ? 'Guardar cambios'
                : 'Crear dashboard'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
